'use client';
import type { CSSProperties, ReactElement } from 'react';
import { CmarkdownLabelText } from '../Cmarkdowntext/CmarkdownLabelText';
import { FontWeight, getFontFamily } from '@/theme/typography';
import { XYCHART_SERIES_COLORS } from './xychart-constants';
import { XYCHART_SERIES_TYPE, type XYChartDataSeries, type XYChartParsed, } from './xychart-types';
import { Cbutton } from '@/components/ui/Cbutton';
import { ButtonPreset } from '@/constants/componentPresets';
const LEGEND_STYLE: CSSProperties = {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    padding: '6px 16px 14px',
    gap: '4px 18px',
};
const LEGEND_BUTTON_BASE_STYLE: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '4px 4px',
    borderRadius: 6,
    color: 'rgba(255,255,255,0.84)',
    fontFamily: getFontFamily(),
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.005em',
    transition: 'opacity 140ms ease-out',
    whiteSpace: 'nowrap',
};
const BAR_SWATCH_STYLE: CSSProperties = {
    width: 8,
    height: 8,
    borderRadius: 2,
    flexShrink: 0,
};
const LINE_SWATCH_STYLE: CSSProperties = {
    width: 14,
    height: 2,
    borderRadius: 1,
    flexShrink: 0,
};
const LEGEND_LABEL_TEXT_STYLE: CSSProperties = {
    color: 'inherit',
    whiteSpace: 'nowrap',
    letterSpacing: '0.005em',
};
const FADED_LEGEND_OPACITY = 0.32;
export interface XYChartLegendProps {
    parsed: XYChartParsed;
    hoveredSeriesIndex: number | null;
    onHoverSeries: (seriesIndex: number | null) => void;
    onHoverCategory: (categoryIndex: number | null) => void;
}
const XY_CHART_LEGEND_ITEM_DEBUG_NAME = 'chartSeriesLegendItem';
function selectSeriesDebugIdentity(input: XYChartDataSeries): string {
    return input.label || `${XY_CHART_LEGEND_ITEM_DEBUG_NAME}-${input.colorIndex}`;
}
function selectSeriesColor(series: XYChartDataSeries): string {
    return XYCHART_SERIES_COLORS[series.colorIndex % XYCHART_SERIES_COLORS.length];
}
function createLegendButtonStyle(input: {
    faded: boolean;
}): CSSProperties {
    return {
        ...LEGEND_BUTTON_BASE_STYLE,
        opacity: input.faded ? FADED_LEGEND_OPACITY : 1,
    };
}
function createLegendSwatchStyle(input: {
    series: XYChartDataSeries;
    color: string;
}): CSSProperties {
    return {
        ...(input.series.type === XYCHART_SERIES_TYPE.Bar
            ? BAR_SWATCH_STYLE
            : LINE_SWATCH_STYLE),
        backgroundColor: input.color,
        backgroundImage: 'none',
    };
}
export function XYChartLegend({ parsed, hoveredSeriesIndex, onHoverSeries, onHoverCategory, }: XYChartLegendProps): ReactElement | null {
    if (parsed.series.length <= 1) {
        return null;
    }
    return (
      <div data-el="Cmarkdownxychart/Legend/Root" style={LEGEND_STYLE}>
        {parsed.series.map((series) => {
            const seriesColor = selectSeriesColor(series);
            const isFaded = hoveredSeriesIndex != null && hoveredSeriesIndex !== series.colorIndex;
            return (<Cbutton rootAttributes={{ "data-el": "Cmarkdownxychart/Legend/Entry" }} key={`legend-${series.colorIndex}`} preset={ButtonPreset.Ghost} ariaLabel={`Highlight ${series.label}`} padding={0} radius={6} disableHoverEffects disableClickMotion style={{ cursor: 'pointer' }} onClick={() => onHoverSeries(series.colorIndex)} onMouseEnter={() => {
                    onHoverSeries(series.colorIndex);
                    onHoverCategory(null);
                }} onMouseLeave={() => {
                    onHoverSeries(null);
                }} containerStyle={createLegendButtonStyle({ faded: isFaded })}>
              <span style={createLegendSwatchStyle({
                    series,
                    color: seriesColor,
                })}/>
              <CmarkdownLabelText dataElScope="Cmarkdownxychart/XYChartLegend" content={series.label} size={12} weight={FontWeight.Medium} paragraphStyle={{ marginBottom: 0 }} textStyle={LEGEND_LABEL_TEXT_STYLE}/>
            </Cbutton>);
        })}
      </div>
    );
}
